import React, { useCallback, useRef } from 'react';
import { SectionRecentlyReadDto, SectionRelayPersonalResponseDto } from '@diritto/common/relay/dtos/section-relay.dtos';
import {
  ContentListType,
  TrackerExtraInfoProps,
  TrackingEventType,
} from '@diritto/common/tracker/tracking-event.constant';
import { useAuthState } from 'contexts/AuthProvider';
import { useTrackingState } from 'contexts/TrackingProvider';
import { useGetSectionsPersonalQuery } from 'queries/relay/useGetSectionsPersonalQuery';
import { useSlide } from 'ui-hooks/useSlide';
import { SectionTitle } from 'app-components/home/sections/components/SectionTitle';
import { SectionRecentlyReadContentTextItem } from 'app-components/home/sections/items/SectionRecentlyReadContentTextItem';
import { SectionSlideButton } from 'app-components/home/sections/components/SectionSlideButton';

const HorizontalGap = 8;

const varient = {
  container: [
    'flex flex-col flex-wrap content-start overflow-x-scroll overflow-y-hidden',
    'h-[135px] px-4 gap-x-2 pc:p-0 pc:mx-[15px] pc:overflow-x-hidden',
    '[&::-webkit-scrollbar]:hidden [scrollbar-width:none] [-ms-overflow-style:none]',
  ].join(' '),
};

type SectionProps = {
  section: SectionRecentlyReadDto;
};

export const SectionRecentlyReadContentTexts = ({
  section,
  trackerExtraInfo,
}: SectionProps & TrackerExtraInfoProps) => {
  const { user, isLoggedIn } = useAuthState();
  const { data, isFetched } = useGetSectionsPersonalQuery(!!user?.isAdult, section.position, isLoggedIn);

  if (user && !isFetched) {
    return (
      <div>
        <SectionTitle section={section} moreLink={section.moreLink} trackerExtraInfo={trackerExtraInfo} />
        <div className={varient.container} />
      </div>
    );
  }

  if (!data || data.recentlyReadContents.length === 0) {
    return null;
  }

  return <SectionRecentlyReadContentTextList section={section} trackerExtraInfo={trackerExtraInfo} data={data} />;
};

const SectionRecentlyReadContentTextList = ({
  section,
  trackerExtraInfo,
  data,
}: SectionProps & TrackerExtraInfoProps & { data: SectionRelayPersonalResponseDto }) => {
  const slideRef = useRef<HTMLDivElement>(null);
  const { handlePrevSlide, handleNextSlide, handleSlide, showSlideButton } = useSlide(slideRef, HorizontalGap);
  const { tracker } = useTrackingState();

  const handleOnClickTracker = useCallback(
    (contentId: number, listItemIndex: number) => {
      tracker.track(TrackingEventType.SelectContentListItem, {
        listType: ContentListType.SectionRecentlyReadContent,
        contentId,
        listItemIndex,
        trackerExtraInfo,
      });
    },
    [tracker, trackerExtraInfo],
  );

  return (
    <div>
      <SectionTitle section={section} moreLink={section.moreLink} trackerExtraInfo={trackerExtraInfo} />
      <div className="relative">
        <SectionSlideButton
          showSlideButton={showSlideButton}
          handleNextSlide={handleNextSlide}
          handlePrevSlide={handlePrevSlide}
        />
        <div className={varient.container} ref={slideRef} onScroll={handleSlide}>
          {data.recentlyReadContents.map((recentlyReadContent, index) => (
            <SectionRecentlyReadContentTextItem
              key={recentlyReadContent.id}
              onClick={() => handleOnClickTracker(recentlyReadContent.contentId, index)}
              recentlyReadContent={recentlyReadContent}
              contents={data.contents}
              episodes={data.episodes}
            />
          ))}
        </div>
      </div>
    </div>
  );
};

export default SectionRecentlyReadContentTexts;
